import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import { Badge, Button, CopyableId, Loading, Tabs } from '@fiado/react-kit';
import { useApplication, useUpdateApplication } from '../../../hooks/useApplications';
import { useApplicationPermissions } from '../../../hooks/useApplicationPermissions';
import { handleApiError } from '../../../api/errorMapping';
import { Banner } from '../../../components/ui/Banner';
import { formatDateMx } from '../../../lib/dateHelpers';
import { LevelsStep, levelsToPayload, type LevelDraft } from './steps/LevelsStep';
import { SeedRolesStep, hasAdminRole, type AvailablePermission } from './steps/SeedRolesStep';
import {
    DefaultsStep,
    isAutoregisterCoherent,
    unknownAutoregisterRoleIds,
} from './steps/DefaultsStep';
import { ApplicationCatalogTab } from './ApplicationCatalogTab';
import type {
    ApplicationDefaultsDraft,
    ApplicationDetailLocal,
    LevelDefLocal,
    SeedRoleWire,
    UpdateApplicationRequestLocal,
} from '../../../api/types/ApplicationLocal';

export type ApplicationDetailTab = 'levels' | 'catalog' | 'roles' | 'defaults';

const TABS: { key: ApplicationDetailTab; label: string }[] = [
    { key: 'levels', label: 'Niveles' },
    { key: 'catalog', label: 'Catálogo' },
    { key: 'roles', label: 'Roles base' },
    { key: 'defaults', label: 'Valores predeterminados' },
];

interface Props {
    applicationId: string;
    /** Pestaña activa (controlada por la ruta vía searchParam, con default 'levels'). */
    tab: ApplicationDetailTab;
    /** Cambia la pestaña activa (la ruta la persiste en el searchParam para deep-link/refresh). */
    onTabChange: (tab: ApplicationDetailTab) => void;
    /** Volver a la lista de aplicaciones. */
    onBack: () => void;
}

/**
 * Detalle de una aplicación (plantilla) con edición por pestañas (#3-a, Task E). Reusa los MISMOS
 * componentes de paso del wizard de crear (LevelsStep / PermissionCatalogStep vía ApplicationCatalogTab /
 * SeedRolesStep / DefaultsStep) pero en modo EDITAR (live):
 *  - Niveles / Roles seed / Defaults → buffer local del tab + botón "Guardar" que persiste con
 *    `PUT /applications/{id}` (`useUpdateApplication`). Cada guardar manda el SNAPSHOT completo del
 *    recurso (displayName + levels + seedRoles + defaults), parcheando solo el campo del tab, para no
 *    pisar los otros campos. `applicationId` es INMUTABLE (no viaja en el `UpdateApplicationRequest`).
 *  - Catálogo → opera LIVE contra los endpoints de permisos (ver `ApplicationCatalogTab`), NO buffer
 *    (el catálogo CUSTOM vive en su propio recurso `/permissions`, no en `Application`).
 *
 * Regla anti-UUID: el dato principal del header es el `displayName`; el `applicationId` (slug) va como
 * CopyableId. El badge "Incompleta" avisa cuando la app no es onboard-able (ningún seedRole con permisos
 * de admin) — cubre el borrador que deja el fallo parcial del wizard (DEC-016).
 */
export function ApplicationDetail({ applicationId, tab, onTabChange, onBack }: Props) {
    const { data, isLoading, isError } = useApplication(applicationId);

    if (isLoading) {
        return (
            <div className="flex justify-center py-10">
                <Loading />
            </div>
        );
    }

    if (isError || !data) {
        return (
            <div className="space-y-3">
                <Button
                    variant="outline"
                    size="sm"
                    leftIcon={<ArrowLeft className="h-4 w-4" />}
                    onClick={onBack}
                >
                    Volver
                </Button>
                <Banner variant="error" title="No se pudo cargar la aplicación">
                    Intenta de nuevo. Si el problema persiste, verifica que la aplicación exista.
                </Banner>
            </div>
        );
    }

    return (
        <ApplicationDetailLoaded app={data} tab={tab} onTabChange={onTabChange} onBack={onBack} />
    );
}

/**
 * Cuerpo con el detalle YA cargado. Se separa para que los buffers locales por tab (levels/seedRoles/
 * defaults) se inicialicen del dato cargado sin condicionar hooks.
 */
function ApplicationDetailLoaded({
    app,
    tab,
    onTabChange,
    onBack,
}: {
    app: ApplicationDetailLocal;
    tab: ApplicationDetailTab;
    onTabChange: (tab: ApplicationDetailTab) => void;
    onBack: () => void;
}) {
    // Onboard-able = al menos un seedRole con permisos de admin. Si no, la app es un borrador
    // incompleto (mismo criterio que gatea el submit del wizard). El badge lo expone.
    const isIncomplete = !hasAdminRole(app.seedRoles);

    const isArchived = app.status === 'ARCHIVED';

    return (
        <div className="space-y-5">
            <div className="space-y-3">
                <Button
                    variant="outline"
                    size="sm"
                    leftIcon={<ArrowLeft className="h-4 w-4" />}
                    onClick={onBack}
                >
                    Aplicaciones
                </Button>

                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="space-y-1">
                        <h1 className="text-2xl font-semibold">{app.displayName}</h1>
                        <CopyableId label="ID" value={app.applicationId} />
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <Badge variant={isArchived ? 'secondary' : 'default'}>
                            {isArchived ? 'Archivada' : 'Activa'}
                        </Badge>
                        {isIncomplete && <Badge variant="warning">Incompleta</Badge>}
                    </div>
                </div>

                {isIncomplete && (
                    <Banner variant="warning" title="Aplicación incompleta">
                        Esta aplicación todavía no está lista para asignarse: necesita al menos un
                        rol con permisos de administración (gestión de roles y usuarios). Complétalo
                        en la pestaña «Roles base».
                    </Banner>
                )}

                <p className="text-xs text-muted-foreground">
                    Última actualización: {app.updatedAt ? formatDateMx(app.updatedAt) : '—'}. El ID
                    de la aplicación es inmutable.
                </p>
            </div>

            <Tabs
                tabs={TABS}
                activeTab={tab}
                onChange={(k) => {
                    onTabChange(k as ApplicationDetailTab);
                }}
            />

            <div className="min-h-[18rem]">
                {tab === 'levels' && <LevelsTab app={app} />}
                {tab === 'catalog' && (
                    <ApplicationCatalogTab applicationId={app.applicationId} levels={app.levels} />
                )}
                {tab === 'roles' && <SeedRolesTab app={app} />}
                {tab === 'defaults' && <DefaultsTab app={app} />}
            </div>
        </div>
    );
}

/* ───────────────────────────── Persistencia compartida (PUT) ───────────────────────────── */

/**
 * Arma el `UpdateApplicationRequest` con el SNAPSHOT completo del recurso (displayName + levels +
 * seedRoles + defaults), parcheando solo lo que cambió el tab. Mandar el snapshot completo evita pisar
 * campos que otro tab editó (el PUT reemplaza los campos presentes). `applicationId` NO viaja (inmutable).
 */
function buildUpdateBody(
    app: ApplicationDetailLocal,
    patch: Partial<UpdateApplicationRequestLocal>,
): UpdateApplicationRequestLocal {
    return {
        displayName: app.displayName,
        levels: app.levels,
        seedRoles: app.seedRoles,
        defaults: app.defaults,
        ...patch,
    };
}

/**
 * Hook compartido por los tabs que persisten con PUT (niveles/roles/defaults). Encapsula la mutation +
 * el toast + un flag `dirty` derivado por el caller. Devuelve un `save(patch)` y el estado de la mutation.
 */
function useTabSave(app: ApplicationDetailLocal) {
    // `useUpdateApplication` ya invalida la lista y el detalle en su onSuccess; aquí solo sumamos el toast.
    const update = useUpdateApplication(app.applicationId);

    const save = (patch: Partial<UpdateApplicationRequestLocal>): void => {
        update.mutate(buildUpdateBody(app, patch), {
            onSuccess: () => {
                toast.success('Cambios guardados');
            },
            onError: handleApiError,
        });
    };

    return { save, isSaving: update.isPending, isError: update.isError };
}

/* ───────────────────────────── Tab: Niveles ───────────────────────────── */

function LevelsTab({ app }: { app: ApplicationDetailLocal }) {
    const { save, isSaving } = useTabSave(app);
    // Buffer local desde el dato cargado (el LevelsStep edita solo displayName; el scope se deriva
    // por posición al persistir, igual que el wizard).
    const initial: LevelDraft[] = useMemo(
        () => app.levels.map((l) => ({ displayName: l.displayName })),
        [app.levels],
    );
    const [levels, setLevels] = useState<LevelDraft[]>(initial);

    const dirty = JSON.stringify(levels) !== JSON.stringify(initial);
    const payload: LevelDefLocal[] = levelsToPayload(levels);
    // No permitir borrar un nivel que un seedRole referencia por scope (el backend lo rechaza).
    const usedLevelScopes = new Set(app.seedRoles.map((r) => r.scope));
    const removedReferencedLevel = app.levels.some(
        (l) => usedLevelScopes.has(l.level) && !payload.some((p) => p.level === l.level),
    );

    return (
        <div className="space-y-4">
            <LevelsStep value={levels} onChange={setLevels} />
            {removedReferencedLevel && (
                <Banner variant="warning">
                    Quitaste un nivel que algún rol base usa como alcance. Reasigna esos roles antes
                    de guardar o el sistema rechazará el cambio.
                </Banner>
            )}
            <SaveBar
                dirty={dirty}
                isSaving={isSaving}
                disabled={removedReferencedLevel}
                onReset={() => {
                    setLevels(initial);
                }}
                onSave={() => {
                    save({ levels: payload });
                }}
            />
        </div>
    );
}

/* ───────────────────────────── Tab: Roles base ───────────────────────────── */

function SeedRolesTab({ app }: { app: ApplicationDetailLocal }) {
    const { save, isSaving } = useTabSave(app);
    const initial = useMemo<SeedRoleWire[]>(() => app.seedRoles, [app.seedRoles]);
    const [seedRoles, setSeedRoles] = useState<SeedRoleWire[]>(initial);

    // El picker ofrece los permisos del catálogo (SYSTEM + CUSTOM). En el detalle, el catálogo vive en
    // su propio recurso; para no acoplar tabs, ofrecemos los permisos que YA usan los roles seed más los
    // que estén presentes — el operador gestiona el catálogo en su pestaña. (El backend valida refs.)
    const {
        data: catalogPerms,
        isError: catalogFailed,
        refetch: refetchCatalog,
    } = useApplicationPermissions(app.applicationId);
    const availablePermissions: AvailablePermission[] = useMemo(() => {
        const byKey = new Map((catalogPerms ?? []).map((p) => [p.permissionKey, p]));
        // Ofrecemos el catálogo completo (con nombre/descripción/categoría reales) + cualquier key que
        // usen los roles seed y no esté en el catálogo (para no ocultarla).
        const keys = new Set<string>((catalogPerms ?? []).map((p) => p.permissionKey));
        for (const r of seedRoles) for (const p of r.permissions) keys.add(p);
        return [...keys].map((k) => {
            const c = byKey.get(k);
            return {
                permissionKey: k,
                displayName: c?.displayName || k,
                description: c?.description,
                category: c?.category,
            };
        });
    }, [seedRoles, catalogPerms]);

    const dirty = JSON.stringify(seedRoles) !== JSON.stringify(initial);
    const adminOk = hasAdminRole(seedRoles);

    return (
        <div className="space-y-4">
            <Banner variant="info">
                Editar los roles base no cambia las organizaciones que ya usan esta aplicación: solo
                aplica a las organizaciones nuevas.
            </Banner>
            {catalogFailed && (
                <Banner variant="error">
                    <span className="flex flex-wrap items-center justify-between gap-2">
                        No pudimos cargar el catálogo de permisos; algunos permisos se muestran sin
                        nombre.
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                void refetchCatalog();
                            }}
                        >
                            Reintentar
                        </Button>
                    </span>
                </Banner>
            )}
            <SeedRolesStep
                value={seedRoles}
                onChange={setSeedRoles}
                availablePermissions={availablePermissions}
            />
            {!adminOk && (
                <Banner variant="warning">
                    Necesitas al menos un rol con permisos de administración para que la aplicación
                    quede lista para asignarse.
                </Banner>
            )}
            <SaveBar
                dirty={dirty}
                isSaving={isSaving}
                disabled={!adminOk}
                onReset={() => {
                    setSeedRoles(initial);
                }}
                onSave={() => {
                    save({ seedRoles });
                }}
            />
        </div>
    );
}

/* ─────────────────────── Tab: Valores predeterminados ─────────────────────── */

function DefaultsTab({ app }: { app: ApplicationDetailLocal }) {
    const { save, isSaving } = useTabSave(app);
    const initial = useMemo<ApplicationDefaultsDraft>(() => app.defaults, [app.defaults]);
    const [defaults, setDefaults] = useState<ApplicationDefaultsDraft>(initial);

    const dirty = JSON.stringify(defaults) !== JSON.stringify(initial);
    const autoregisterOk = isAutoregisterCoherent(defaults.autoregister, app.seedRoles);
    const unknownRoles = unknownAutoregisterRoleIds(defaults.autoregister, app.seedRoles);

    return (
        <div className="space-y-4">
            <DefaultsStep
                value={defaults}
                onChange={setDefaults}
                levels={app.levels}
                availableSeedRoles={app.seedRoles}
            />
            {!autoregisterOk && (
                <Banner variant="warning">
                    El autorregistro usa {String(unknownRoles.length)} rol(es) que no existen entre
                    los roles base. Corrígelo (o ajusta los roles base) antes de guardar.
                </Banner>
            )}
            <SaveBar
                dirty={dirty}
                isSaving={isSaving}
                disabled={!autoregisterOk}
                onReset={() => {
                    setDefaults(initial);
                }}
                onSave={() => {
                    save({ defaults: defaults as ApplicationDetailLocal['defaults'] });
                }}
            />
        </div>
    );
}

/* ───────────────────────────── Barra de guardado ───────────────────────────── */

function SaveBar({
    dirty,
    isSaving,
    disabled,
    onReset,
    onSave,
}: {
    dirty: boolean;
    isSaving: boolean;
    disabled?: boolean;
    onReset: () => void;
    onSave: () => void;
}) {
    return (
        <div className="flex items-center justify-end gap-2 border-t pt-4">
            <div className="mr-auto text-xs text-muted-foreground" aria-live="polite">
                {dirty ? 'Tienes cambios sin guardar.' : 'Sin cambios pendientes.'}
            </div>
            <Button type="button" variant="outline" disabled={!dirty || isSaving} onClick={onReset}>
                Descartar
            </Button>
            <Button
                type="button"
                isLoading={isSaving}
                disabled={!dirty || disabled || isSaving}
                onClick={onSave}
            >
                Guardar
            </Button>
        </div>
    );
}
